import { effect, Injectable, signal, WritableSignal } from "@angular/core";
import { NavigationService } from "@apps/patient-core/src/app/services/navigation.service";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { BaseCommonService } from "@apps/patient-core/src/app/services/abstracts/common.service";
import { Subject, Subscription, debounceTime, filter } from "rxjs";
import { CacheService } from "@apps/patient-core/src/app/services/cache.service";
import { ConciergeService } from "./concierge.service";
import { OverlayService } from "@apps/patient-core/src/app/services/overlay.service";
import { ConciergeMetricsService } from "./analytics/concierge-metrics.service";
import { NhsPrService } from "@apps/patient-core/src/app/services/nhs-pr.service";
import { BaseAuthService } from "@apps/patient-core/src/app/services/abstracts/auth.service";
import { ActivityMonitorService } from "@apps/patient-core/src/app/services/activity-monitor.service";
import { Constants } from "@apps/concierge-app/constants";
import { LoginInactivityModalComponent } from "../components/login/login-inactivity-modal/login-inactivity-modal.component";
import { AccessLevel } from "@dentally/enums/access-level";
import { filterDefined } from "@apps/patient-core/src/app/utils/rxjs";
import { ConciergeSpecificAuthService } from "./concierge-specific-auth.service";
import { AuthSessionType } from "@dentally/auth/auth-types";
import { GRAPHQL_OPERATION_NAMES, HttpService } from "@apps/patient-core/src/app/services/http.service";
import { KioskCheckInBase } from "@backend/graph/kiosk_check_in/kiock-check-in-base";
import { BasePatientsService } from "@apps/patient-core/src/app/services/abstracts/patients.service";
import { logger } from "@dentally/ui/logger";
import tz from "dayjs/plugin/timezone";
import { PatientActionsCacheService } from "@apps/patient-core/src/app/services/patient-actions-cache.service";

dayjs.extend(advancedFormat);
dayjs.extend(tz);

@Injectable({
  providedIn: "root",
})
export class ConciergeLoginService {
  public onLogout = new Subject<void>();
  private _kioskData: WritableSignal<KioskCheckInBase | null> = signal(null);
  public kioskData = this._kioskData.asReadonly();

  constructor(
    private _navigationService: NavigationService,
    private _authService: BaseAuthService,
    private _commonService: BaseCommonService,
    private _conciergeMetricsService: ConciergeMetricsService,
    private _cacheService: CacheService,
    private _conciergeService: ConciergeService,
    private _activityMonitorService: ActivityMonitorService,
    private _overlayService: OverlayService,
    private _nhsPrService: NhsPrService,
    private _conciergeSpecificAuthService: ConciergeSpecificAuthService,
    private _httpService: HttpService,
    private _patientsService: BasePatientsService,
    private _patientActionsCacheService: PatientActionsCacheService
  ) {
    effect(() => {
      if (this._nhsPrService.saved()) {
        if (this.hasShortcut) {
          this.navigate("thank-you");
        }
      }
    });
  }
  private _patientDirections: string;
  private _day: number | null = null;
  private _month: number | null = null;
  private _year: string | null = null;
  private _surnameInitial: string | null = null;
  private _firstName: string | null = null;
  private _init: boolean;
  private _inactivityMonitorSubscription: Subscription;
  private _jwtTokenChangedSubscription: Subscription;

  public get init(): boolean {
    return this._init;
  }

  public set init(init: boolean) {
    this._init = init;
  }

  public set day(day: number | null) {
    this._day = day;
  }
  public get day(): number | null {
    return this._day;
  }

  public set month(month: number | null) {
    this._month = month;
  }

  public get month(): number | null {
    return this._month;
  }

  public set year(year: string | null) {
    this._year = year;
  }

  public get year(): string | null {
    return this._year;
  }

  public set surnameInitial(surnameInitial: string | null) {
    this._surnameInitial = surnameInitial;
  }

  public get surnameInitial(): string | null {
    return this._surnameInitial;
  }

  public get firstName(): string | null {
    return this._firstName;
  }

  public set firstName(firstName: string | null) {
    this._firstName = firstName;
  }

  public get dateOfBirthFormatted(): string {
    return dayjs(this.dateOfBirth).format("Do MMMM YYYY");
  }

  public get dateOfBirth(): string {
    if (!this._year || !this._month || !this._day) {
      throw new Error("Date of birth missing one of year, month or day");
    }

    return `${this._year}-${this._month.toString().padStart(2, "0")}-${this._day.toString().padStart(2, "0")}`;
  }

  public get appointmentTime(): string {
    if (this._kioskData()) {
      return dayjs(this._kioskData()?.visit?.appointment_start_time).tz(this._commonService.practice.time_zone).format("h:mma");
    }
    return "";
  }

  public startCheckIn(): void {
    if (this._isKioskMode) {
      this.navigate("enter-day");
      return;
    }

    this.navigate("enter-dob");
  }

  public handleDobEntry(dob: string): void {
    const year = dob.split("/")[2];
    const day = dob.split("/")[0];
    const month = dob.split("/")[1];

    this.year = year;
    this.day = +day;
    this.month = +month;

    this._conciergeMetricsService.trackCheckInDobEntered();

    this.navigate("enter-surname");
  }

  public reset(): void {
    this._day = null;
    this._month = null;
    this._year = null;
    this._surnameInitial = null;
    this._firstName = null;
  }

  private _handleSearchError(message: string) {
    logger.error(message);
    this.navigate("error");
  }

  private get _isKioskMode(): boolean {
    return this._conciergeService.deviceMode === AuthSessionType.KIOSK;
  }

  private _trackCheckedIn(): void {
    if (this._isKioskMode) {
      const isLate = !!this._kioskData()?.late;
      const hasTasks = !!this._kioskData()?.visit?.actions?.length;

      this._conciergeMetricsService.trackKioskCheckIn({ is_late: isLate, has_tasks: hasTasks });
    } else {
      this._conciergeMetricsService.trackCheckedIn();
    }
  }

  private async _handleSearchSuccess(): Promise<void> {
    this._trackCheckedIn();
    this.startInactivityMonitor();

    if (this._isKioskMode) {
      const path = this._kioskData()?.late ? "late" : "welcome";
      this._navigationService.navigate(`/kiosk-${path}`);
      return;
    }

    await this._patientsService.getPatient();

    this._navigationService.navigate(`/my-dental${this.shortcut}`);
  }

  private _handleSearchFailure(locked: boolean): void {
    if (locked) return;

    this._conciergeMetricsService.trackCheckInPatientNotFound(!!this.firstName);

    if (this.firstName) {
      this.navigate("no-match");
      return;
    }

    this.navigate("enter-first-name");
  }

  public search() {
    const { surnameInitial, dateOfBirth } = this;
    const { urlSiteId } = this._commonService;

    if (!surnameInitial || !dateOfBirth || !urlSiteId) {
      return this._handleSearchError(`Missing surname initial (${surnameInitial}), date of birth (${dateOfBirth}) or site id (${urlSiteId})`);
    }

    const device_mode = this._conciergeService.deviceMode;

    if (device_mode === AuthSessionType.CONCIERGE) {
      this._handleConciergeLogin();
    } else {
      this._handleKioskCheckin();
    }
  }

  private _handleKioskCheckin() {
    const query = `
  {
      kiosk_check_in(
        date_of_birth: "${this.dateOfBirth}"
        patient_identifier: "${this.surnameInitial}"
        site_id: "${this._commonService.urlSiteId}"
        ${this._firstName ? `first_name: "${this._firstName}"` : ""}
      ) {
        success
        locked
        late
        patient_directions
        visit {
          appointment_start_time
          patient_name
          practitioner_name
          actions
        }
      }
    }
  `;

    this._httpService.query(GRAPHQL_OPERATION_NAMES.KIOSK_CHECK_IN, query).subscribe({
      next: (response) => {
        if (response.errors) {
          this._handleSearchError(response.errors[0].message);
          return;
        }
        const { success, locked } = response.data.kiosk_check_in;

        if (success) {
          this._kioskData.set(response.data.kiosk_check_in);
          void this._handleSearchSuccess();
          return;
        }

        this._handleSearchFailure(locked);
      },
      error: (error) => {
        this._handleSearchError(error);
      },
    });
  }

  private _handleConciergeLogin() {
    try {
      this._conciergeSpecificAuthService
        .login({
          date_of_birth: this.dateOfBirth,
          surname_initial: this.surnameInitial!,
          site_id: this._commonService.urlSiteId,
          first_name: this._firstName ?? undefined,
        })
        .subscribe({
          next: (response) => {
            if ("accessToken" in response) {
              this._authService.setToken(response.accessToken);
              void this._handleSearchSuccess();
              return;
            }

            this._handleSearchFailure(response.locked);
          },
          error: (error) => {
            this._handleSearchError(error);
          },
        });
    } catch (error) {
      this._handleSearchError(error);
    }
  }

  public navigate(route?: string) {
    const path = this._conciergeService.deviceLocked ? "locked" : route;
    this._navigationService.navigate(`/login/${path || ""}`);
  }

  /**
   * Start the inactivity monitor and logout the user if they are inactive for 60 seconds
   */
  public startInactivityMonitor(): void {
    if (this._inactivityMonitorSubscription) this._inactivityMonitorSubscription.unsubscribe();
    if (this._jwtTokenChangedSubscription) this._jwtTokenChangedSubscription.unsubscribe();

    logger.info("starting inactivity monitor for patient in practice");

    this._inactivityMonitorSubscription = this._activityMonitorService.activity
      .pipe(
        debounceTime(Constants.CONCIERGE_INACTIVITY_MONITOR_INTERVAL),
        filter(() => this._authService.isPatientInPractice())
      )
      .subscribe(() => {
        this._inactivityMonitorSubscription.unsubscribe();
        this._overlayService.open({
          component: LoginInactivityModalComponent,
        });
      });

    this._jwtTokenChangedSubscription = this._authService.onJWTChanged
      .pipe(
        filterDefined(),
        filter((change) => !!change.jwt)
      )
      .subscribe((change) => {
        if (change.jwt.access_level === AccessLevel.PUBLIC_IN_PRACTICE) {
          this._inactivityMonitorSubscription.unsubscribe();
        }
      });
  }

  public get shortcut(): string {
    return this._cacheService.get(Constants.CONCIERGE_LOGIN_SHORTCUT_STORAGE_KEY) || "";
  }

  public get hasShortcut(): boolean {
    return !!this.shortcut;
  }

  /**
   * Renames the current (portal in practice) jwt and replaces it with the patient in practice jwt
   */
  public loginPatientInPractice(token: string) {
    const publicInPracticeJwt = this._cacheService.get("jwt");
    this._cacheService.set("public_in_practice_jwt", publicInPracticeJwt);
    this._authService.setToken(token);
  }

  public navigateToLogout(): void {
    this._navigationService.navigate("/login/logout");
  }

  public async logoutPatientInPractice(trigger: string): Promise<void> {
    if (!this._authService.isPatientInPractice()) {
      this._conciergeService.navigateToGetStarted();

      return;
    }

    this.onLogout.next();
    await this._conciergeSpecificAuthService.logout(trigger);
    this._cacheService.delete(Constants.CONCIERGE_LOGIN_SHORTCUT_STORAGE_KEY);
    this._patientActionsCacheService.clear();
    this._cacheService.delete(Constants.INACTIVITY_TIMER_END_TIME_STORAGE_KEY);
    this._conciergeService.navigateToGetStarted();
  }
}
